export type TagSortKey = "name" | "threads" | "lastUsed" | "created"
export type TagSortDirection = "asc" | "desc"

export type TagSortType =
	| "name-asc"
	| "name-desc"
	| "threads-asc"
	| "threads-desc"
	| "lastUsed-asc"
	| "lastUsed-desc"
	| "created-asc"
	| "created-desc"

export interface TagStat {
	threadCount: number
	lastUsedAt: string | null
}

export interface SortableTag {
	id: string
	name: string
	createdAt: number
}

export type TagStats = Record<string, TagStat | undefined>

export const DEFAULT_TAG_SORT: TagSortType = "name-asc"

export function parseTagSort(sortType: TagSortType): {
	key: TagSortKey
	direction: TagSortDirection
} {
	const [key, direction] = sortType.split("-") as [TagSortKey, TagSortDirection]
	return { key, direction }
}

function lastUsedValue(stat: TagStat | undefined): number | null {
	if (!stat?.lastUsedAt) return null
	const time = new Date(stat.lastUsedAt).getTime()
	return Number.isNaN(time) ? null : time
}

// A tag no thread carries has no date to compare, so it sits last either way
// rather than jumping to the top when the direction flips.
function compare(
	a: SortableTag,
	b: SortableTag,
	sortType: TagSortType,
	stats: TagStats,
): number {
	const { key, direction } = parseTagSort(sortType)
	const flip = direction === "asc" ? 1 : -1

	if (key === "name") {
		return (
			a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) * flip
		)
	}
	if (key === "created") {
		return (a.createdAt - b.createdAt) * flip
	}
	if (key === "threads") {
		const left = stats[a.id]?.threadCount ?? 0
		const right = stats[b.id]?.threadCount ?? 0
		if (left === right) return a.name.localeCompare(b.name)
		return (left - right) * flip
	}

	const left = lastUsedValue(stats[a.id])
	const right = lastUsedValue(stats[b.id])
	if (left === null && right === null) return a.name.localeCompare(b.name)
	if (left === null) return 1
	if (right === null) return -1
	if (left === right) return a.name.localeCompare(b.name)
	return (left - right) * flip
}

export function sortTags<T extends SortableTag>(
	tags: readonly T[],
	sortType: TagSortType,
	stats: TagStats,
): T[] {
	return [...tags].sort((a, b) => compare(a, b, sortType, stats))
}

export function filterTags<T extends SortableTag>(
	tags: readonly T[],
	query: string,
): T[] {
	const needle = query.trim().toLowerCase()
	if (!needle) return [...tags]
	return tags.filter((tag) => tag.name.toLowerCase().includes(needle))
}
